import { useQuery } from "@tanstack/react-query";
import { Phone, MessageCircle, Mail, Clock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Page, PageTitle } from "@/components/site/Common";
import { ContactForm } from "@/components/site/ContactForm";
import { useMe } from "@/hooks/useMe";
import { get } from "@/lib/api";
import { CONTACT, fdate } from "@/lib/hz";

const FAQ = [
  ["¿Cómo pido un presupuesto?", "Publica tu trabajo indicando el oficio, la descripción y tu ciudad. Los profesionales de ese oficio te enviarán presupuestos con precio y plazo; tú aceptas el que prefieras."],
  ["¿Puedo elegir yo al profesional?", "Sí. Busca en el directorio, entra en su perfil y envía una solicitud directa. Te responderá con su precio para aceptar o rechazar."],
  ["¿Qué pasa si el profesional no se presenta?", "Abre una incidencia de «Profesional no presentado». Asignamos con urgencia un sustituto de la misma especialidad sin coste adicional."],
  ["¿Cómo pago?", "Al terminar, el profesional emite la factura con IVA 21%. La pagas con tarjeta de forma segura y puedes descargarla en PDF."],
  ["¿Cómo se controla la calidad?", "Revisas el trabajo con nuestra checklist (acabado, comprobación técnica, limpieza, puntualidad y trato) y lo valoras. Si algo no está bien, abres una incidencia y respondemos en 24 h."],
  ["¿Es la misma cuenta que en la app?", "Sí. Esta web está conectada en directo con la app HandyZoom: mismos usuarios, trabajos, presupuestos y facturas."],
];

const MyIncidents = () => {
  const q = useQuery({ queryKey: ["incidents", "mine"], queryFn: () => get("/incidents/mine") });
  if (!q.data?.length) return null;
  return (
    <div className="mt-12" data-testid="my-incidents">
      <h2 className="font-heading text-xl font-bold">Mis incidencias</h2>
      <div className="mt-4 space-y-3">
        {q.data.map((i) => (
          <div key={i.id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-2"><div className="font-semibold">{i.category} · {i.job_title}</div><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize">{i.status}</span></div>
            <p className="mt-1 text-sm text-slate-600">{i.description}</p>
            {i.resolution && <p className="mt-2 text-sm text-emerald-700">{i.resolution}</p>}
            <div className="mt-1 text-xs text-slate-400">{fdate(i.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Support() {
  const { data: me } = useMe();
  const channels = [
    [Phone, CONTACT.phoneDisplay, `tel:${CONTACT.phone}`, "support-phone"],
    [MessageCircle, `WhatsApp ${CONTACT.whatsappDisplay}`, CONTACT.whatsappUrl, "support-whatsapp"],
    [Mail, CONTACT.email, `mailto:${CONTACT.email}`, "support-email"],
  ];
  return (
    <Page>
      <PageTitle eyebrow="Atención al cliente" title="Estamos para ayudarte" sub={`Atención ${CONTACT.hours}.`} />
      <div className="mt-8 grid gap-4 md:grid-cols-4">
        {channels.map(([Icon, label, href, tid]) => (
          <a key={tid} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={tid} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-orange-300"><Icon className="size-5 text-orange-600" /><div className="mt-3 font-semibold">{label}</div></a>
        ))}
        <div className="rounded-2xl bg-slate-950 p-5 text-white"><Clock className="size-5 text-orange-500" /><div className="mt-3 font-semibold">{CONTACT.hours}</div></div>
      </div>
      <div className="mt-12 grid gap-10 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-xl font-bold">Preguntas frecuentes</h2>
          <Accordion type="single" collapsible className="mt-4" data-testid="faq">
            {FAQ.map(([qq, a], i) => (
              <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-semibold" data-testid={`faq-${i}`}>{qq}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>
            ))}
          </Accordion>
        </div>
        <div><h2 className="mb-4 font-heading text-xl font-bold">Escríbenos</h2><ContactForm /></div>
      </div>
      {me && <MyIncidents />}
    </Page>
  );
}
